import { type FC, useCallback, useMemo, useState } from 'react';
import { Button, Dropdown, Switch } from 'antd';
import type { MenuProps } from 'antd';
import { EllipsisOutlined, ProductOutlined } from '@ant-design/icons';

import FilterIcon from 'assets/svg/Filter';
import NewGlobalFilter from 'components/molecules/common/NewGlobalFilter/NewGlobalFilter';
import FilterRibbon from 'components/molecules/PricingSimulator/FilterRibbon/FilterRibbon';
import { tpoGenUrls } from 'config/urls';
import { transformFiltersV2 } from 'utils/helperMethods';
import type { PlanIndexFilterState } from 'utils/planIndexFilterParams';

import { useTpoGenConfig } from '../context/CustomerConfigContext';
import { resolveFieldLabel } from '../utils/resolveFieldLabel';
import FieldSelectPopover from '../components/FieldSelectPopover';
import type { PromoLibraryConfig, PromoLibraryKpiColumnConfig } from '../config/promoLibraryConfig';

const FILTER_ATTRIBUTE_PAGE_SIZE = 50;

const HANDLED_ACTIONS = new Set(['export', 'reset_promo_library', 'reset_library']);

export interface PromoLibraryToolbarProps {
    config: PromoLibraryConfig;
    kpiColumns: PromoLibraryKpiColumnConfig[];
    selectedKpis: string[];
    setSelectedKpis: (value: string[]) => void;
    showChange: boolean;
    setShowChange: (value: boolean) => void;
    allCheckedAttribute: PlanIndexFilterState['allCheckedAttribute'];
    allSelectedValueForAllAttribute: PlanIndexFilterState['allSelectedValueForAllAttribute'];
    applyFilterObject: PlanIndexFilterState['applyFilterObject'];
    setAppliedFilters: (result: PlanIndexFilterState) => void;
    resetFilters: () => void;
    isExporting: boolean;
    onAction: (key: string) => void;
}

const PromoLibraryToolbar: FC<PromoLibraryToolbarProps> = ({
    config,
    kpiColumns,
    selectedKpis,
    setSelectedKpis,
    showChange,
    setShowChange,
    allCheckedAttribute,
    allSelectedValueForAllAttribute,
    applyFilterObject,
    setAppliedFilters,
    resetFilters,
    isExporting,
    onAction,
}) => {
    const { fieldValueLabelMaps } = useTpoGenConfig();
    const { labels, filterKeys, filterDimensionInfo } = config;

    const [selectedFilter, setSelectedFilter] = useState<{ label: string; value: string } | null>(null);
    const [tempApplyFilterObject, setTempApplyFilterObject] = useState<Record<string, unknown>>({});

    const kpiOptions = useMemo(
        () => kpiColumns.map((column) => ({ key: column.key, label: column.label })),
        [kpiColumns],
    );

    const filterKeyMap = useMemo(() => new Map(filterKeys.map((key) => [key.value, key])), [filterKeys]);

    const filterApiVariables = useMemo(() => {
        if (!selectedFilter?.value) return undefined;
        return {
            attribute_type: selectedFilter.value,
            limit: FILTER_ATTRIBUTE_PAGE_SIZE,
            filters: transformFiltersV2(tempApplyFilterObject, filterKeys, selectedFilter.value),
        };
    }, [selectedFilter, tempApplyFilterObject, filterKeys]);

    const filterDataParser = useCallback(
        (rawItems: string[], resolveLabelFrom?: string) =>
            rawItems.map((name) => ({
                label: resolveFieldLabel(name, fieldValueLabelMaps, resolveLabelFrom),
                value: name,
            })),
        [fieldValueLabelMaps],
    );

    const renderFilterAttribute = useCallback(
        (key: string, attribute: string | number) => (
            <span className="py-1.5">
                {resolveFieldLabel(String(attribute), fieldValueLabelMaps, filterKeyMap.get(key)?.resolveLabelFrom)}
            </span>
        ),
        [fieldValueLabelMaps, filterKeyMap],
    );

    const actionMenuItems = useMemo<MenuProps['items']>(
        () =>
            config.actionMenuItems.map((item) => ({
                key: item.key,
                label: item.key === 'export' && isExporting ? 'Exporting...' : item.label,
                disabled: !HANDLED_ACTIONS.has(item.key) || (item.key === 'export' && isExporting),
            })),
        [config.actionMenuItems, isExporting],
    );

    return (
        <>
            <div className="flex flex-row flex-wrap justify-between">
                <div className="flex flex-col gap-y-[0.5rem]">
                    <p className="text-xl font-semibold text-[#18181B]">{labels.sectionTitle}</p>
                </div>

                <div className="flex items-center gap-x-[0.6rem]">
                    {config.showChangeToggle ? (
                        <div className="flex items-center gap-[0.75rem]">
                            <p className="text-xs text-[#807E86]">{labels.showChange}</p>
                            <Switch checked={showChange} onChange={setShowChange} />
                        </div>
                    ) : null}

                    <NewGlobalFilter
                        availableFilters={filterKeys}
                        allCheckedAttribute={allCheckedAttribute}
                        allSelectedValueForAllAttribute={allSelectedValueForAllAttribute}
                        applyFilterObject={applyFilterObject}
                        resetFilterObject={resetFilters}
                        onSubmitHandler={setAppliedFilters}
                        url={tpoGenUrls.getPromoLibraryFilterAttributes}
                        selectedFilter={selectedFilter}
                        setSelectedFilter={setSelectedFilter}
                        tempApplyFilterObject={tempApplyFilterObject}
                        setTempApplyFilterObject={setTempApplyFilterObject}
                        apiVariables={filterApiVariables}
                        filterDimensionInfo={filterDimensionInfo}
                        showAttributePanelToLeft
                        filterIcon={<FilterIcon />}
                        dataParser={filterDataParser}
                    />

                    <FieldSelectPopover
                        title={labels.selectKpis}
                        options={kpiOptions}
                        selected={selectedKpis}
                        onCommit={setSelectedKpis}
                        selectAllLabel={labels.selectAll}
                        deselectAllLabel={labels.deselectAll}
                    >
                        <Button>
                            <div className="flex flex-row items-center gap-x-[0.5rem]">
                                <ProductOutlined />
                                <p>{labels.kpis}</p>
                            </div>
                        </Button>
                    </FieldSelectPopover>

                    <Dropdown
                        menu={{ items: actionMenuItems, onClick: ({ key }) => onAction(key) }}
                        trigger={['click']}
                        overlayStyle={{ width: '12rem', borderRadius: '0.5rem' }}
                        disabled={!actionMenuItems?.length}
                    >
                        <Button>
                            <div className="flex flex-row items-center justify-start">
                                <EllipsisOutlined />
                            </div>
                        </Button>
                    </Dropdown>
                </div>
            </div>

            <div className="mt-3 mb-3">
                <FilterRibbon
                    allCheckedAttribute={allCheckedAttribute}
                    allSelectedValueForAllAttribute={allSelectedValueForAllAttribute}
                    applyFilterObject={applyFilterObject}
                    resetFilterObject={resetFilters}
                    onSubmitHandler={setAppliedFilters}
                    filterKeys={filterKeys}
                    renderAttribute={renderFilterAttribute}
                />
            </div>
        </>
    );
};

PromoLibraryToolbar.displayName = 'PromoLibraryToolbar';

export default PromoLibraryToolbar;
